import React, { useEffect, useState } from "react";
import logger from "../lib/logger";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import { Eyebrow } from "../components/Primitives";

function Section({ children, className = "", ...rest }) {
    return (
        <section className={`px-6 sm:px-12 py-16 sm:py-24 ${className}`} {...rest}>
            <div className="max-w-3xl mx-auto">{children}</div>
        </section>
    );
}

export default function FoundingLanding() {
    const [stats, setStats] = useState({ spots_remaining: 100, spots_claimed: 0, spots_total: 100, closed: false });
    const [faqOpen, setFaqOpen] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const s = await api.foundingStats();
                setStats(s);
            } catch (err) {
                logger.warn("founding stats fetch failed:", err);
            }
        })();
    }, []);

    const goClaim = () => navigate("/");

    const FAQS = [
        {
            q: "What does the $99 cover?",
            a: "Your $99 secures founding member status and immediate access to your full compatibility profile during our beta. When we officially launch, that $99 becomes a credit applied to any subscription tier you choose. Your founding access doesn't lapse in the meantime — you keep using the platform throughout beta at no additional cost.",
        },
        {
            q: "What happens when you launch?",
            a: "We'll notify you in advance with full pricing details. You'll choose whichever subscription tier fits you best, and your $99 credit will apply automatically. There are no surprise charges and no auto-enrollment.",
        },
        {
            q: "Is the assessment scientifically valid?",
            a: "Yes. Our compatibility assessment is based on a well-established personality framework validated across decades of peer-reviewed research, refined in partnership with a board-certified psychologist for use in a relational context.",
        },
        {
            q: "Can I get a refund?",
            a: "Yes. If Ever After isn't for you, request a refund within 30 days for any reason. After 30 days, your $99 credit remains on your account through launch.",
        },
        {
            q: "What's Privé?",
            a: "Privé is our future white-glove tier — private introductions, a dedicated matchmaker, and concierge date planning for senior professionals. Founding members receive priority consideration when Privé opens.",
        },
        {
            q: "When does the founding offer close?",
            a: "When we hit 100 members or when we officially launch — whichever comes first.",
        },
    ];

    return (
        <div className="min-h-screen bg-cream text-ink" data-testid="founding-landing">
            {/* Top nav */}
            <header className="px-6 sm:px-12 py-6 flex items-center justify-between sticky top-0 bg-cream/90 backdrop-blur-md z-30 border-b border-burgundy/10">
                <Logo size={48} />
                <button
                    data-testid="nav-claim-btn"
                    onClick={goClaim}
                    className="bg-burgundy text-cream uppercase text-[10px] tracking-[0.24em] px-4 py-2.5 hover:bg-burgundy-deep transition-colors"
                >
                    Claim my spot
                </button>
            </header>

            {/* HERO */}
            <Section className="text-center sm:text-left">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                    <div className="text-[10px] tracking-[0.36em] uppercase text-gold-deep" data-testid="hero-eyebrow">
                        Founding membership — 100 spots
                    </div>
                    <h1 className="mt-5 font-display text-5xl sm:text-7xl leading-[1.02] text-burgundy tracking-tight">
                        <span className="italic">You're</span> not bad at dating.<br />
                        The apps are bad at matching you.
                    </h1>
                    <p className="mt-6 text-lg sm:text-xl text-ink-muted leading-relaxed max-w-2xl font-light">
                        Ever After is the first dating platform built on the science of how people actually connect — not who they swipe right on at 1am. Be one of the first 100 women or men to help shape it.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
                        <button
                            data-testid="hero-cta-btn"
                            onClick={goClaim}
                            className="bg-burgundy text-cream uppercase text-xs tracking-[0.22em] px-7 py-4 hover:bg-burgundy-deep transition-colors"
                        >
                            Claim my founding spot — $99
                        </button>
                        <p className="text-[11px] tracking-[0.28em] uppercase text-ink-muted" data-testid="hero-counter">
                            Limited to 100 members · {stats.spots_remaining} spots remaining
                        </p>
                    </div>
                </motion.div>
            </Section>

            {/* PROBLEM */}
            <Section className="bg-cream-soft border-y border-burgundy/10">
                <Eyebrow>The problem</Eyebrow>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy">
                    <span className="italic">You've</span> done the work. The apps haven't.
                </h2>
                <div className="mt-8 font-display text-xl text-ink leading-relaxed space-y-5 max-w-2xl">
                    <p>You've read the books. You've been to therapy. You know your attachment style, your patterns, what you actually want. And yet the apps keep showing you the same five people who say "fluent in sarcasm" in their bio.</p>
                    <p>Swipe culture wasn't built for people who know themselves. It was built for volume.</p>
                    <p className="italic text-burgundy">Ever After is built for the opposite.</p>
                </div>
            </Section>

            {/* SCIENCE */}
            <Section>
                <Eyebrow>The science</Eyebrow>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy">
                    <span className="italic">Compatibility</span>, measured.
                </h2>
                <p className="mt-6 text-lg text-ink leading-relaxed max-w-2xl">
                    Our proprietary compatibility assessment maps you across six personality dimensions validated by decades of psychological research and refined with a board-certified psychologist. In under five minutes, you'll discover your love archetype — and the archetype of the person who'll actually fit your life.
                </p>
                <p className="mt-3 font-display italic text-lg text-ink-muted max-w-2xl">
                    This isn't a personality quiz. It's the same science used in clinical research, designed for the way you actually date.
                </p>

                <div className="mt-12 grid sm:grid-cols-3 gap-3">
                    {[
                        { title: "Your archetype", body: "Discover which of eight love archetypes you embody, and what that means for how you give and receive love." },
                        { title: "Your ideal match profile", body: "See the archetypes most likely to be compatible with yours, and the ones that look exciting but lead nowhere." },
                        { title: "Your blind spots", body: "The patterns you keep repeating without knowing why. Named, explained, and finally interrupted." },
                    ].map((f, i) => (
                        <div key={f.title} className="border border-burgundy/15 bg-cream p-6" data-testid={`feature-card-${i}`}>
                            <div className="font-display italic text-burgundy text-xl">{f.title}</div>
                            <p className="mt-3 text-sm text-ink leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* OFFER */}
            <Section className="bg-burgundy text-cream">
                <div className="text-[10px] tracking-[0.36em] uppercase text-gold">The founding offer</div>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.1]">
                    <span className="italic">Founding 100.</span> One time. $99.
                </h2>
                <p className="mt-6 text-lg leading-relaxed max-w-2xl text-cream/90">
                    We're inviting the first 100 women and men to help us build Ever After from the inside. Your $99 founding fee becomes a $99 credit on any plan when we officially launch — and your access never lapses.
                </p>
                <div className="mt-10 grid sm:grid-cols-2 gap-8">
                    <div>
                        <div className="text-[10px] tracking-[0.28em] uppercase text-gold">What you get today</div>
                        <ul className="mt-3 space-y-2 text-base font-display text-cream/95 leading-snug">
                            <li>— Immediate access to your full compatibility assessment + archetype reveal</li>
                            <li>— Early access to all matching features as we release them</li>
                            <li>— A direct line to the founder — your feedback shapes the product</li>
                            <li>— Founding Member status on your profile (visible to other members)</li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-[10px] tracking-[0.28em] uppercase text-gold">What you get at launch</div>
                        <ul className="mt-3 space-y-2 text-base font-display text-cream/95 leading-snug">
                            <li>— Guaranteed place in your city's first matching cohort — before the general waitlist</li>
                            <li>— $99 credit applied to any subscription tier you choose</li>
                            <li>— Founder Preferred Rate locked at 25% below public pricing</li>
                            <li>— Priority consideration for Privé when it opens</li>
                        </ul>
                    </div>
                </div>
                <div className="mt-12 flex flex-col items-start gap-3">
                    <button
                        data-testid="offer-cta-btn"
                        onClick={goClaim}
                        className="bg-gold text-burgundy uppercase text-xs tracking-[0.22em] px-7 py-4 hover:bg-gold-deep transition-colors"
                    >
                        Claim my founding spot
                    </button>
                    <p className="text-[11px] tracking-[0.28em] uppercase text-cream/80">
                        {stats.spots_remaining} of {stats.spots_total || 100} spots remaining · Founding access closes when we hit 100
                    </p>
                </div>
            </Section>

            {/* WHO */}
            <Section>
                <Eyebrow>Who this is for</Eyebrow>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy">
                    <span className="italic">Built for the</span> people who know what they're looking for.
                </h2>
                <div className="mt-10 space-y-6 font-display text-2xl text-ink leading-snug max-w-2xl">
                    <p>They're tired of explaining themselves to people who don't read.</p>
                    <p>They want a partner, not a project.</p>
                    <p>They're done with apps that treat them like a product, and ready for one that treats them like a person.</p>
                </div>
            </Section>

            {/* FOUNDER */}
            <Section className="bg-cream-soft border-y border-burgundy/10">
                <Eyebrow>A note from our founder</Eyebrow>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy italic">
                    Why I built this.
                </h2>
                <div className="mt-8 text-lg text-ink leading-relaxed space-y-5 max-w-2xl font-light">
                    <p>I built Ever After because I was exhausted.</p>
                    <p>Exhausted by the same conversations on the same apps with the same people I wasn't compatible with — who played games or didn't look like their profile photo. Exhausted by a dating industry that profits when we stay single.</p>
                    <p>I'm a senior legal contract negotiator and now a founder, and I've spent the last year building Ever After with a board-certified psychologist, validating every dimension of our compatibility science. We're not another swipe app. We're not a matchmaker you can't afford. We're something new.</p>
                    <p>If you're one of the first 100, you're not just buying access. You're helping us build the dating platform we all wished existed.</p>
                    <p className="font-display italic text-2xl text-burgundy">I'm so glad you're here.</p>
                </div>
                <p className="mt-8 font-display italic text-lg text-burgundy">— Teresa</p>
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Founder &amp; CEO, Ever After</p>
            </Section>

            {/* FAQ */}
            <Section>
                <Eyebrow>FAQ</Eyebrow>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy">
                    <span className="italic">Questions</span> we hear most.
                </h2>
                <div className="mt-10 border-t border-burgundy/15">
                    {FAQS.map((f, i) => {
                        const open = faqOpen === i;
                        return (
                            <div key={f.q} className="border-b border-burgundy/15" data-testid={`faq-row-${i}`}>
                                <button
                                    data-testid={`faq-toggle-${i}`}
                                    onClick={() => setFaqOpen(open ? null : i)}
                                    className="w-full flex items-center justify-between text-left py-5"
                                >
                                    <span className="font-display text-xl text-burgundy pr-4">{f.q}</span>
                                    <span className="text-burgundy text-2xl font-display leading-none">{open ? "–" : "+"}</span>
                                </button>
                                {open && (
                                    <p className="pb-6 pr-8 text-base text-ink leading-relaxed font-light">
                                        {f.a}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Section>

            {/* CLOSING CTA */}
            <Section className="text-center bg-cream-soft border-t border-burgundy/10">
                <h2 className="font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy">
                    100 women and men. <span className="italic">One science.</span><br />
                    A new way to find your person.
                </h2>
                <div className="mt-10 inline-flex flex-col items-center gap-3">
                    <button
                        data-testid="closing-cta-btn"
                        onClick={goClaim}
                        className="bg-burgundy text-cream uppercase text-xs tracking-[0.22em] px-9 py-4 hover:bg-burgundy-deep transition-colors"
                    >
                        Claim my founding spot — $99
                    </button>
                    <p className="text-[11px] tracking-[0.28em] uppercase text-ink-muted" data-testid="closing-counter">
                        {stats.spots_remaining} spots remaining
                    </p>
                </div>
            </Section>

            <footer className="px-6 sm:px-12 py-10 text-center border-t border-burgundy/10">
                <Logo size={36} className="mx-auto" />
                <p className="mt-4 text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    © Ever After · <a href="mailto:info@youreverafter.com" className="underline-offset-2 hover:underline">info@youreverafter.com</a>
                </p>
            </footer>
        </div>
    );
}
